import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Edit, Settings, Users, FolderTree, Image, GripVertical, LogOut, FileText, Upload, Download } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function AdminPanel({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');

  // Helper function to format error messages properly
  const formatErrorMessage = (error, fallbackMessage = 'An error occurred') => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    } else if (Array.isArray(detail)) {
      return detail[0]?.msg || fallbackMessage;
    } else if (typeof detail === 'object' && detail !== null) {
      return detail.msg || fallbackMessage;
    }
    return error.message || fallbackMessage;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Header */}
          <div className="flex md:hidden justify-between items-center py-3">
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-to-projects-button" size="sm">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Admin Panel</h1>
                <p className="text-xs text-gray-500">Manage settings</p>
              </div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="logout-button" className="flex items-center gap-2 text-sm" size="sm">
                  <LogOut className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Logout</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to logout?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onLogout}>Logout</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          
          {/* Desktop Header */}
          <div className="hidden md:grid grid-cols-3 items-center py-3 gap-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-to-projects-button-desktop" size="sm">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
                <p className="text-sm text-gray-500">Manage system settings</p>
              </div>
            </div>
            <div className="flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_admin-user-portal-2/artifacts/9fookd3l_Logo%20Datastorm%20Blue%20with%20Transparent%20BG%20Cropped.png" 
                alt="Datastorm Logo" 
                className="h-16 w-auto"
              />
            </div>
            <div className="flex items-center gap-2 justify-end">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="logout-button-desktop" className="flex items-center gap-2 text-sm" size="sm">
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Logout</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to logout?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onLogout}>Logout</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-4xl mx-auto">
            <TabsTrigger value="users" data-testid="users-tab" className="flex items-center gap-2" title="Users">
              <Users className="w-4 h-4 md:w-4 md:h-4" />
              <span className="hidden md:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="csv-templates" data-testid="csv-templates-tab" className="flex items-center gap-2" title="Templates">
              <FileText className="w-4 h-4 md:w-4 md:h-4" />
              <span className="hidden md:inline">Templates</span>
            </TabsTrigger>
            <TabsTrigger value="export" data-testid="export-tab" className="flex items-center gap-2" title="Export Settings">
              <Image className="w-4 h-4 md:w-4 md:h-4" />
              <span className="hidden md:inline">Export</span>
            </TabsTrigger>
            <TabsTrigger value="deleted" data-testid="deleted-tab" className="flex items-center gap-2" title="Deleted Projects">
              <Trash2 className="w-4 h-4 md:w-4 md:h-4" />
              <span className="hidden md:inline">Deleted</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <UserManagement />
          </TabsContent>

          <TabsContent value="csv-templates">
            <CSVTemplateManagement />
          </TabsContent>

          <TabsContent value="export">
            <ExportSettingsPanel />
          </TabsContent>

          <TabsContent value="deleted">
            <DeletedProjects />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'user',
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axiosInstance.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Load users error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(formatErrorMessage(error, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editUser) {
        await axiosInstance.put(`/admin/users/${editUser.id}`, formData);
        toast.success('User updated');
      } else {
        await axiosInstance.post('/admin/users', formData);
        toast.success('User created');
      }
      setShowModal(false);
      setEditUser(null);
      setFormData({ username: '', email: '', full_name: '', password: '', role: 'user' });
      loadUsers();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Operation failed'));
    }
  };

  const handleEdit = (user) => {
    setEditUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      full_name: user.full_name || '',
      password: '',
      role: user.role,
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user?')) return;

    try {
      await axiosInstance.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      loadUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>User Management</CardTitle>
            <CardDescription>Add, edit, and remove users</CardDescription>
          </div>
          <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-user-button"
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                onClick={() => {
                  setEditUser(null);
                  setFormData({ username: '', email: '', password: '', role: 'user' });
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="user-modal">
              <DialogHeader>
                <DialogTitle>{editUser ? 'Edit User' : 'Add New User'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username *</Label>
                  <Input
                    id="username"
                    data-testid="user-username-input"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    data-testid="user-email-input"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    data-testid="user-fullname-input"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password {editUser && '(leave blank to keep current)'}</Label>
                  <Input
                    id="password"
                    data-testid="user-password-input"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editUser}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role *</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" data-testid="user-submit-button" className="w-full">
                  {editUser ? 'Update User' : 'Create User'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <div className="space-y-2">
            {users.map((user) => (
              <div
                key={user.id}
                data-testid={`user-item-${user.id}`}
                className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div>
                  <div className="font-semibold text-gray-900">{user.full_name || user.username}</div>
                  <div className="hidden md:block text-sm text-gray-600">{user.email}</div>
                  {user.full_name && <div className="text-xs text-gray-500">@{user.username}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs px-3 py-1 rounded-full font-medium ${
                      user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {user.role}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(user)}
                    data-testid={`edit-user-${user.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(user.id)}
                    data-testid={`delete-user-${user.id}`}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TemplateManagement() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);
  const [deleteCategoryInfo, setDeleteCategoryInfo] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ id: null, name: '', order: 0 });
  const [itemForm, setItemForm] = useState({
    id: null,
    category_id: '',
    name: '',
    enabled: true,
    required: true,
    order: 0,
  });

  useEffect(() => {
    loadCategories();
    loadAllItems();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axiosInstance.get('/templates/categories');
      setCategories(response.data);
      if (response.data.length > 0 && !selectedCategory) {
        setSelectedCategory(response.data[0]);
      }
    } catch (error) {
      console.error('Load categories error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(formatErrorMessage(error, 'Failed to load categories'));
    }
  };

  const loadAllItems = async () => {
    try {
      const response = await axiosInstance.get('/templates/items');
      setItems(response.data);
    } catch (error) {
      console.error('Load items error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(formatErrorMessage(error, 'Failed to load items'));
    }
  };
  
  const getItemsByCategory = (categoryId) => {
    return items.filter(item => item.category_id === categoryId);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/templates/categories', categoryForm);
      toast.success('Category saved');
      setShowCategoryModal(false);
      setCategoryForm({ id: null, name: '', order: 0 });
      loadCategories();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/templates/items', itemForm);
      toast.success('Item saved');
      setShowItemModal(false);
      setItemForm({ id: null, category_id: '', name: '', enabled: true, required: true, order: 0 });
      loadAllItems();
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      // Get the count of items in this category
      const countResponse = await axiosInstance.get(`/templates/categories/${categoryId}/items/count`);
      const itemCount = countResponse.data.count;
      
      // Set state to show confirmation dialog
      setDeleteCategoryId(categoryId);
      setDeleteCategoryInfo({ itemCount });
    } catch (error) {
      console.error('Delete category error:', error);
      toast.error('Failed to load category info');
    }
  };

  const confirmDeleteCategory = async () => {
    try {
      const response = await axiosInstance.delete(`/templates/categories/${deleteCategoryId}`);
      
      if (response.data.deleted_items > 0) {
        toast.success(`Category and ${response.data.deleted_items} item${response.data.deleted_items > 1 ? 's' : ''} deleted`);
      } else {
        toast.success('Category deleted');
      }
      
      loadCategories();
      loadAllItems();
      setSelectedCategory(null);
      setDeleteCategoryId(null);
      setDeleteCategoryInfo(null);
    } catch (error) {
      console.error('Delete category error:', error);
      toast.error(formatErrorMessage(error, 'Failed to delete category'));
    }
  };

  const handleDeleteItem = (itemId) => {
    setDeleteItemId(itemId);
  };

  const confirmDeleteItem = async () => {
    try {
      await axiosInstance.delete(`/templates/items/${deleteItemId}`);
      toast.success('Item deleted');
      loadAllItems();
      setDeleteItemId(null);
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleCategoryDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = categories.findIndex(c => c.id === active.id);
      const newIndex = categories.findIndex(c => c.id === over.id);
      
      const newCategories = arrayMove(categories, oldIndex, newIndex);
      setCategories(newCategories);
      
      // Update order in backend
      try {
        const updates = newCategories.map((cat, index) => ({
          id: cat.id,
          order: index
        }));
        await axiosInstance.post('/templates/categories/reorder', { updates });
        toast.success('Categories reordered');
      } catch (error) {
        toast.error('Failed to reorder categories');
        loadCategories(); // Reload on error
      }
    }
  };

  const handleItemDragEnd = (categoryId) => async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      // Get items for this specific category
      const categoryItems = items.filter(i => i.category_id === categoryId);
      const oldIndex = categoryItems.findIndex(i => i.id === active.id);
      const newIndex = categoryItems.findIndex(i => i.id === over.id);
      
      // Reorder within category
      const reorderedCategoryItems = arrayMove(categoryItems, oldIndex, newIndex);
      
      // Update the full items list
      const otherItems = items.filter(i => i.category_id !== categoryId);
      const newItems = [...otherItems, ...reorderedCategoryItems];
      setItems(newItems);
      
      // Update order in backend (only for this category's items)
      try {
        const updates = reorderedCategoryItems.map((item, index) => ({
          id: item.id,
          order: index
        }));
        await axiosInstance.post('/templates/items/reorder', { updates });
        toast.success('Items reordered');
      } catch (error) {
        toast.error('Failed to reorder items');
        loadAllItems(); // Reload on error
      }
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Categories */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Categories</CardTitle>
              <CardDescription>Organize photo templates</CardDescription>
            </div>
            <Button
              size="sm"
              data-testid="add-category-button"
              onClick={() => {
                setCategoryForm({ id: null, name: '', order: categories.length });
                setShowCategoryModal(true);
              }}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleCategoryDragEnd}
          >
            <SortableContext
              items={categories.map(c => c.id)}
              strategy={verticalListSortingStrategy}
            >
              {categories.map((cat) => (
                <SortableCategoryItem
                  key={cat.id}
                  cat={cat}
                  isSelected={selectedCategory?.id === cat.id}
                  onSelect={() => setSelectedCategory(cat)}
                  onEdit={(cat) => {
                    setCategoryForm(cat);
                    setShowCategoryModal(true);
                  }}
                  onDelete={handleDeleteCategory}
                />
              ))}
            </SortableContext>
          </DndContext>
        </CardContent>
      </Card>

      {/* Items by Category */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Template Items</CardTitle>
              <CardDescription>Grouped by category - drag to reorder within category</CardDescription>
            </div>
            <Button
              size="sm"
              data-testid="add-item-button"
              onClick={() => {
                setItemForm({
                  id: null,
                  category_id: categories[0]?.id || '',
                  name: '',
                  enabled: true,
                  required: true,
                  order: 0,
                });
                setShowItemModal(true);
              }}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 max-h-[600px] overflow-y-auto">
          {categories.map((category) => {
            const categoryItems = getItemsByCategory(category.id);
            return (
              <div key={category.id} className="border rounded-lg p-3 bg-gray-50">
                <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center justify-between">
                  <span>{category.name} ({categoryItems.length})</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setItemForm({
                        id: null,
                        category_id: category.id,
                        name: '',
                        enabled: true,
                        required: true,
                        order: categoryItems.length,
                      });
                      setShowItemModal(true);
                    }}
                    className="h-6 w-6 p-0"
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </h4>
                {categoryItems.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleItemDragEnd(category.id)}
                  >
                    <SortableContext
                      items={categoryItems.map(i => i.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-1">
                        {categoryItems.map((item) => (
                          <SortableTemplateItem
                            key={item.id}
                            item={item}
                            onEdit={(item) => {
                              setItemForm(item);
                              setShowItemModal(true);
                            }}
                            onDelete={handleDeleteItem}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <p className="text-xs text-gray-400 italic">No items in this category</p>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent data-testid="category-modal">
          <DialogHeader>
            <DialogTitle>{categoryForm.id ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cat-name">Name *</Label>
              <Input
                id="cat-name"
                data-testid="category-name-input"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-order">Order</Label>
              <Input
                id="cat-order"
                data-testid="category-order-input"
                type="number"
                value={categoryForm.order}
                onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) })}
              />
            </div>
            <Button type="submit" data-testid="category-submit-button" className="w-full">
              Save Category
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Item Modal */}
      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent data-testid="item-modal">
          <DialogHeader>
            <DialogTitle>{itemForm.id ? 'Edit Item' : 'Add Item'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleItemSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="item-name">Name *</Label>
              <Input
                id="item-name"
                data-testid="item-name-input"
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="item-category">Category *</Label>
              <Select 
                value={itemForm.category_id || ""} 
                onValueChange={(value) => setItemForm({ ...itemForm, category_id: value })}
              >
                <SelectTrigger data-testid="item-category-select">
                  <SelectValue placeholder="Select a category..." />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="item-enabled">Enabled</Label>
              <Switch
                id="item-enabled"
                data-testid="item-enabled-switch"
                checked={itemForm.enabled}
                onCheckedChange={(checked) => setItemForm({ ...itemForm, enabled: checked })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="item-required">Required</Label>
              <Switch
                id="item-required"
                data-testid="item-required-switch"
                checked={itemForm.required}
                onCheckedChange={(checked) => setItemForm({ ...itemForm, required: checked })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="item-order">Order</Label>
              <Input
                id="item-order"
                data-testid="item-order-input"
                type="number"
                value={itemForm.order}
                onChange={(e) => setItemForm({ ...itemForm, order: parseInt(e.target.value) })}
              />
            </div>
            <Button type="submit" data-testid="item-submit-button" className="w-full">
              Save Item
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Delete Confirmation */}
      <AlertDialog open={!!deleteCategoryId} onOpenChange={() => {
        setDeleteCategoryId(null);
        setDeleteCategoryInfo(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteCategoryInfo && deleteCategoryInfo.itemCount > 0 ? (
                <>
                  This category contains <strong>{deleteCategoryInfo.itemCount} item{deleteCategoryInfo.itemCount > 1 ? 's' : ''}</strong>. 
                  Deleting this category will also delete all associated items. This action cannot be undone.
                </>
              ) : (
                'This action cannot be undone. This will permanently delete the category.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteCategory} className="bg-red-600 hover:bg-red-700">
              Delete Category
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Item Delete Confirmation */}
      <AlertDialog open={!!deleteItemId} onOpenChange={() => setDeleteItemId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template Item?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the template item.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteItem} className="bg-red-600 hover:bg-red-700">
              Delete Item
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ExportSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axiosInstance.get('/export-settings');
      setSettings(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error('Load settings error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(formatErrorMessage(error, 'Failed to load settings'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.put('/export-settings', formData);
      toast.success('Settings updated');
      loadSettings();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading settings...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Export Settings</CardTitle>
        <CardDescription>Configure image processing and export parameters</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Image Variants Generated on Upload</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Thumb:</strong> 300×400px (Card previews)</li>
            <li>• <strong>Medium:</strong> 600×800px (Modal previews)</li>
            <li>• <strong>Large:</strong> 900×1200px (Master/Export)</li>
          </ul>
          <p className="text-xs text-blue-700 mt-2">All variants use 3:4 portrait aspect ratio</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="format">Image Format</Label>
              <Select
                value={formData.image_format}
                onValueChange={(value) => setFormData({ ...formData, image_format: value })}
              >
                <SelectTrigger data-testid="format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="WEBP">WebP</SelectItem>
                  <SelectItem value="JPEG">JPEG</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quality">Quality (1-100)</Label>
              <Input
                id="quality"
                data-testid="quality-input"
                type="number"
                min="1"
                max="100"
                value={formData.quality}
                onChange={(e) => setFormData({ ...formData, quality: parseInt(e.target.value) })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="width">Master Width (px)</Label>
              <Input
                id="width"
                data-testid="width-input"
                type="number"
                value={formData.resize_width}
                onChange={(e) => setFormData({ ...formData, resize_width: parseInt(e.target.value) })}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500">Fixed at 900px (Portrait 3:4)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="height">Master Height (px)</Label>
              <Input
                id="height"
                data-testid="height-input"
                type="number"
                value={formData.resize_height}
                onChange={(e) => setFormData({ ...formData, resize_height: parseInt(e.target.value) })}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500">Fixed at 1200px (Portrait 3:4)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="font-size">Font Size (px)</Label>
              <Input
                id="font-size"
                data-testid="font-size-input"
                type="number"
                value={formData.font_size_px}
                onChange={(e) => setFormData({ ...formData, font_size_px: parseInt(e.target.value) })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="font-color">Font Color (Hex)</Label>
              <Input
                id="font-color"
                data-testid="font-color-input"
                value={formData.font_color_hex}
                onChange={(e) => setFormData({ ...formData, font_color_hex: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="caption-bg">Caption Background (Hex)</Label>
              <Input
                id="caption-bg"
                data-testid="caption-bg-input"
                value={formData.caption_bg_hex}
                onChange={(e) => setFormData({ ...formData, caption_bg_hex: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center justify-between">
              <Label htmlFor="strip-exif">Strip EXIF Data</Label>
              <Switch
                id="strip-exif"
                data-testid="strip-exif-switch"
                checked={formData.strip_exif}
                onCheckedChange={(checked) => setFormData({ ...formData, strip_exif: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="burn-project-name">Burn-in Project Name</Label>
              <Switch
                id="burn-project-name"
                data-testid="burn-project-name-switch"
                checked={formData.burn_in_project_name}
                onCheckedChange={(checked) => setFormData({ ...formData, burn_in_project_name: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="burn-timestamp">Burn-in Timestamp</Label>
              <Switch
                id="burn-timestamp"
                data-testid="burn-timestamp-switch"
                checked={formData.burn_in_timestamp}
                onCheckedChange={(checked) => setFormData({ ...formData, burn_in_timestamp: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="caption-outside">Burn-in Caption</Label>
              <Switch
                id="caption-outside"
                data-testid="caption-outside-switch"
                checked={formData.caption_outside_with_bg}
                onCheckedChange={(checked) => setFormData({ ...formData, caption_outside_with_bg: checked })}
              />
            </div>
          </div>

          <Button type="submit" data-testid="save-settings-button" className="w-full">
            Save Settings
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// Sortable Category Item Component
function SortableCategoryItem({ cat, isSelected, onSelect, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: cat.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`category-item-${cat.id}`}
      className={`flex justify-between items-center p-3 rounded-lg cursor-pointer transition-colors ${
        isSelected ? 'bg-blue-100 border-2 border-blue-500' : 'bg-gray-50 hover:bg-gray-100'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2 flex-1">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing touch-none"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-5 h-5 text-gray-400" />
        </button>
        <span className="font-medium text-gray-900">{cat.name}</span>
      </div>
      <div className="flex gap-1">
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(cat);
          }}
          data-testid={`edit-category-${cat.id}`}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(cat.id);
          }}
          data-testid={`delete-category-${cat.id}`}
          className="text-red-600"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// Sortable Template Item Component
function SortableTemplateItem({ item, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`item-${item.id}`}
      className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
    >
      <div className="flex items-center gap-2 flex-1">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-5 h-5 text-gray-400" />
        </button>
        <div className="flex-1">
          <div className="font-medium text-gray-900">{item.name}</div>
          <div className="flex gap-2 mt-1">
            <span className={`text-xs px-2 py-0.5 rounded-full ${item.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
              {item.enabled ? 'Enabled' : 'Disabled'}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${item.required ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
              {item.required ? 'Required' : 'Optional'}
            </span>
          </div>
        </div>
      </div>
      <div className="flex gap-1">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onEdit(item)}
          data-testid={`edit-item-${item.id}`}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDelete(item.id)}
          data-testid={`delete-item-${item.id}`}
          className="text-red-600"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function DeletedProjects() {
  const [deletedProjects, setDeletedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoreId, setRestoreId] = useState(null);
  const [permanentDeleteId, setPermanentDeleteId] = useState(null);

  // Helper function to format error messages
  const formatErrorMessage = (error, fallbackMessage = 'An error occurred') => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    } else if (Array.isArray(detail)) {
      return detail[0]?.msg || fallbackMessage;
    } else if (typeof detail === 'object' && detail !== null) {
      return detail.msg || fallbackMessage;
    }
    return error.message || fallbackMessage;
  };

  useEffect(() => {
    loadDeletedProjects();
  }, []);

  const loadDeletedProjects = async () => {
    try {
      const response = await axiosInstance.get('/projects?include_deleted=true');
      const deleted = response.data.filter(p => p.deleted);
      setDeletedProjects(deleted);
    } catch (error) {
      console.error('Load deleted projects error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(formatErrorMessage(error, 'Failed to load deleted projects'));
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    try {
      await axiosInstance.post(`/projects/${restoreId}/restore`);
      toast.success('Project restored');
      loadDeletedProjects();
      setRestoreId(null);
    } catch (error) {
      toast.error('Failed to restore project');
    }
  };

  const handlePermanentDelete = async () => {
    try {
      await axiosInstance.delete(`/projects/${permanentDeleteId}/permanent`);
      toast.success('Project permanently deleted');
      loadDeletedProjects();
      setPermanentDeleteId(null);
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Deleted Projects</CardTitle>
          <CardDescription>Restore or permanently delete projects</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : deletedProjects.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Trash2 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No deleted projects</p>
            </div>
          ) : (
            <div className="space-y-3">
              {deletedProjects.map((project) => (
                <div key={project.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-semibold text-gray-900">{project.name}</h4>
                    <p className="text-sm text-gray-500">
                      Customer: {project.customer || 'N/A'} • Date: {project.date}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Deleted: {new Date(project.deleted_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRestoreId(project.id)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      Restore
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPermanentDeleteId(project.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Delete Forever
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restore Confirmation */}
      <AlertDialog open={!!restoreId} onOpenChange={() => setRestoreId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Project?</AlertDialogTitle>
            <AlertDialogDescription>
              This project will be restored and visible again in the project list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore}>Restore</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent Delete Confirmation */}
      <AlertDialog open={!!permanentDeleteId} onOpenChange={() => setPermanentDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently Delete Project?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the project and all associated photos and data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePermanentDelete} className="bg-red-600 hover:bg-red-700">
              Delete Forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


function CSVTemplateManagement() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);
  
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    csvFile: null,
    csvContent: ''
  });
  
  const [csvPreview, setCsvPreview] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await axiosInstance.get('/admin/csv-templates');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load CSV templates');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        toast.error('File too large (max 1MB)');
        return;
      }
      
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target.result;
        setUploadForm({ ...uploadForm, csvFile: file, csvContent: content });
        
        // Parse and preview CSV
        try {
          const lines = content.split('\n');
          const headers = lines[0].split(',');
          const preview = lines.slice(1, 6).map(line => line.split(','));
          setCsvPreview({ headers, rows: preview, totalRows: lines.length - 1 });
        } catch (error) {
          toast.error('Failed to parse CSV');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    
    if (!uploadForm.csvContent) {
      toast.error('Please select a CSV file');
      return;
    }

    try {
      await axiosInstance.post('/admin/csv-templates', {
        name: uploadForm.name,
        description: uploadForm.description,
        csv_content: uploadForm.csvContent
      });
      
      toast.success('CSV template uploaded successfully');
      setShowUploadModal(false);
      setUploadForm({ name: '', description: '', csvFile: null, csvContent: '' });
      setCsvPreview(null);
      loadTemplates();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to upload template'));
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      await axiosInstance.put(`/admin/csv-templates/${editingTemplate.id}`, {
        name: editingTemplate.name,
        description: editingTemplate.description
      });
      
      toast.success('Template updated successfully');
      setShowEditModal(false);
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to update template'));
    }
  };

  const handleDelete = async () => {
    if (!deleteTemplateId) return;
    
    try {
      await axiosInstance.delete(`/admin/csv-templates/${deleteTemplateId}`);
      toast.success('Template deleted successfully');
      setDeleteTemplateId(null);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleDownloadTemplate = async (templateId) => {
    try {
      const response = await axiosInstance.get(`/admin/csv-templates/${templateId}`);
      const csvContent = response.data.csv_content;
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${response.data.name.replace(/\s+/g, '_')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (error) {
      toast.error('Failed to download CSV');
    }
  };

  const handleDownloadBoilerplate = async () => {
    try {
      const response = await axiosInstance.get('/csv-templates/download-boilerplate', {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'photo_template_boilerplate.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Boilerplate CSV downloaded');
    } catch (error) {
      toast.error('Failed to download boilerplate');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading templates...</div>;
  }

  return (
    <>
      <Card>
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold">CSV Templates</h2>
              <p className="text-sm text-gray-600 mt-1">Upload and manage CSV photo templates</p>
            </div>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <Button onClick={handleDownloadBoilerplate} variant="outline" className="flex-1 sm:flex-none">
                <Download className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Download Example</span>
                <span className="sm:hidden">Example</span>
              </Button>
              <Button onClick={() => setShowUploadModal(true)} className="flex-1 sm:flex-none">
                <Upload className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Upload Template</span>
                <span className="sm:hidden">Upload</span>
              </Button>
            </div>
          </div>

          {/* Templates List */}
          {templates.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-4">No CSV templates yet</p>
              <Button onClick={() => setShowUploadModal(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Upload Your First Template
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => (
                <Card key={template.id} className={!template.is_active ? 'opacity-50' : ''}>
                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <h3 className="font-semibold text-lg">{template.name}</h3>
                      </div>
                      {!template.is_active && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                          Deleted
                        </span>
                      )}
                    </div>
                    
                    {template.description && (
                      <p className="text-sm text-gray-600">{template.description}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{template.category_count} Categories</span>
                      <span>•</span>
                      <span>{template.slot_count} Slots</span>
                    </div>
                    
                    <div className="text-xs text-gray-400">
                      Created {new Date(template.created_at).toLocaleDateString()}
                    </div>
                    
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(template)}
                        className="flex-1 min-w-[80px]"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadTemplate(template.id)}
                        className="flex-1 min-w-[100px]"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        <span className="hidden sm:inline">Download</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeleteTemplateId(template.id)}
                        className="text-red-600 hover:text-red-700 px-3"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload CSV Template</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                value={uploadForm.name}
                onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                placeholder="e.g., Real Estate Premium"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="template-description">Description (optional)</Label>
              <Textarea
                id="template-description"
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Brief description of this template"
                rows={2}
              />
            </div>
            
            <div>
              <Label>CSV File *</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Max 1MB • Format: Category, Caption, Required
              </p>
            </div>
            
            {csvPreview && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-sm mb-2">Preview ({csvPreview.totalRows} rows)</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="border-b">
                        {csvPreview.headers.map((header, i) => (
                          <th key={i} className="text-left p-2 font-semibold">{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.rows.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-b">
                          {row.map((cell, j) => (
                            <td key={j} className="p-2">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {csvPreview.totalRows > 5 && (
                  <p className="text-xs text-gray-500 mt-2">
                    ... and {csvPreview.totalRows - 5} more rows
                  </p>
                )}
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                <Upload className="w-4 h-4 mr-2" />
                Upload Template
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
          </DialogHeader>
          {editingTemplate && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Template Name</Label>
                <Input
                  id="edit-name"
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editingTemplate.description || ''}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                  rows={2}
                />
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">Template Stats:</span>
                </div>
                <div className="space-y-1 text-gray-600">
                  <div>{editingTemplate.category_count} Categories</div>
                  <div>{editingTemplate.slot_count} Photo Slots</div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTemplateId} onOpenChange={() => setDeleteTemplateId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete CSV Template?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the template from the library. Projects already created from this template will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default AdminPanel;
